import React, { useEffect, useRef, useState } from 'react';
import '../styles/TtnContainer.css';
import '../styles/DeliveryNote.css';
import {
    deliveryNoteGetAPI,
    deliveryNoteSaveAPI,
    DeliveryNoteEntity,
    DeliveryNoteFields,
} from '../http/deliveryNoteAPI';
import { REACT_APP_API_URL } from '../config';
import { setCookie } from '../utils/cookies';

// «Видаткова накладна» блока в окне расчёта. Живёт своим состоянием — окно
// расчёта рендерится через общий Popup как готовый JSX, так что форма не может
// держать state в CalculationsContainer.
type Props = {
    calcId: number,
    // Номер счёта расчёта: пока его нет, накладную сформировать нельзя. Смена
    // значения (счёт только что сформировали) перезапрашивает дефолты.
    invoice: string | number | null,
    canEdit: boolean,
};

const FIELDS: Array<{ key: keyof DeliveryNoteFields, label: string, required?: boolean, wide?: boolean, placeholder?: string }> = [
    { key: 'number', label: 'Номер накладної', required: true, placeholder: 'напр. 75' },
    { key: 'date', label: 'Дата', required: true, placeholder: 'дд/мм/рррр' },
    { key: 'basis', label: 'Підстава', wide: true },
    { key: 'contract', label: 'Договір', wide: true },
    { key: 'responsibleName', label: 'Відповідальний за видаткову накладну (ПІБ)', wide: true,
        placeholder: 'напр. Чаюн Яніна Євгеніївна, головний бухгалтер' },
    { key: 'responsiblePhone', label: 'Телефон відповідального', placeholder: 'напр. +38 (097) 445 90 82' },
    { key: 'place', label: 'Місце складання', wide: true },
];

const EMPTY: DeliveryNoteFields = {
    number: '', date: '', basis: '', contract: '', responsibleName: '', responsiblePhone: '', place: '',
};

// The date is typed as дд/мм/рррр (a native <input type="date"> shows the
// browser locale's order, which can't be changed) and sent as YYYY-MM-DD.
const isoToDisplay = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

const displayToIso = (text: string): string | null => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text.trim());
    if (!m) return null;
    const [, dd, mm, yyyy] = m;
    const d = new Date(Date.UTC(+yyyy, +mm - 1, +dd));
    // Rejects 31/02/2024 and the like — Date would silently roll them over.
    if (d.getUTCFullYear() !== +yyyy || d.getUTCMonth() !== +mm - 1 || d.getUTCDate() !== +dd) return null;
    return `${yyyy}-${mm}-${dd}`;
};

// Digits only, slashes inserted as you type: "3001202" → "30/01/202".
const maskDate = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join('/');
};

// Form state keeps the date in its display form (дд/мм/рррр).
const pickFields = (src: Partial<DeliveryNoteFields> | null | undefined): DeliveryNoteFields => {
    const out = { ...EMPTY };
    for (const k of Object.keys(EMPTY) as Array<keyof DeliveryNoteFields>) {
        out[k] = `${src?.[k] ?? ''}`;
    }
    out.date = isoToDisplay(out.date);
    return out;
};

const openMedia = () => {
    // Медиа отдаётся по cookie-JWT — как ссылка на PDF счёта.
    const token = localStorage.getItem('token');
    if (token) setCookie('cookieJwt', token, 7);
};

const DeliveryNoteSection = ({ calcId, invoice, canEdit }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState<DeliveryNoteEntity | null>(null);
    const [blockedReason, setBlockedReason] = useState<string | null>(null);
    const [form, setForm] = useState<DeliveryNoteFields>(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const datePickerRef = useRef<HTMLInputElement>(null);
    const dateInputId = `deliveryNote-date-${calcId}`;

    const load = () => {
        setLoading(true);
        deliveryNoteGetAPI(calcId).then((res: any) => {
            setLoading(false);
            if (res?.status !== 200 || !res.data) {
                setError(res?.response?.data?.message || 'Не удалось загрузить накладную');
                return;
            }
            setError(null);
            setNote(res.data.note ?? null);
            setBlockedReason(res.data.blockedReason ?? null);
            setForm(pickFields(res.data.note ?? res.data.defaults));
        });
    };

    useEffect(load, [calcId, invoice]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.number.trim()) { setError('Укажите номер накладной'); return; }
        const isoDate = displayToIso(form.date);
        if (!isoDate) { setError('Укажите дату накладной в формате дд/мм/рррр'); return; }
        setSaving(true);
        const res: any = await deliveryNoteSaveAPI(calcId, { ...form, date: isoDate });
        setSaving(false);
        if (res?.status !== 200 || !res.data) {
            setError(res?.response?.data?.message || 'Не удалось сформировать накладную');
            return;
        }
        setError(null);
        setNote(res.data);
        setForm(pickFields(res.data));
    };

    const pdfLink = note?.pdfFile ? (
        <a href={`${REACT_APP_API_URL}media/${note.pdfFile}`} target='_blank' rel='noreferrer' onClick={openMedia}>
            {note.pdfFile}
        </a>
    ) : null;

    const disabled = !canEdit || saving || !!blockedReason;

    const openDatePicker = () => {
        const el = datePickerRef.current;
        if (!el) return;
        el.value = displayToIso(form.date) ?? '';
        try {
            el.showPicker();
        } catch {
            el.focus();
            el.click();
        }
    };

    return (
        <div className='deliveryNote'>
            <div className='deliveryNote__head'>
                <button type='button' className='deliveryNote__toggle' onClick={() => setIsOpen((v) => !v)}
                        aria-expanded={isOpen}>
                    <span className='deliveryNote__chevron'>{isOpen ? '▾' : '▸'}</span>
                    Видаткова накладна
                </button>
                <span className='deliveryNote__summary'>
                    {loading ? 'Загрузка…' : note ? <>№{note.number} · {pdfLink ?? 'PDF не сформирован'}</> : 'не сформирована'}
                </span>
            </div>

            {isOpen && !loading && (
                <form className='deliveryNote__body' onSubmit={submit}>
                    {blockedReason && <div className='deliveryNote__notice'>{blockedReason}</div>}
                    <fieldset className='deliveryNote__fields' disabled={disabled}>
                        {FIELDS.map((f) => (
                            <label key={f.key} className={`ttnForm__field${f.wide ? ' deliveryNote__field-wide' : ''}`}
                                   // The date's first control is the calendar button — point the
                                   // caption at the text field instead.
                                   htmlFor={f.key === 'date' ? dateInputId : undefined}>
                                <span className='ttnForm__label'>{f.label}{f.required ? '*' : ''}</span>
                                {f.key === 'date' ? (
                                    <span className='deliveryNote__date'>
                                        <button type='button' className='nav__button btn-ghost deliveryNote__date-btn'
                                                title='Вибрати в календарі' onClick={openDatePicker}>📅</button>
                                        <input className='input ttnForm__input'
                                               id={dateInputId}
                                               type='text'
                                               inputMode='numeric'
                                               placeholder={f.placeholder}
                                               value={form.date}
                                               onChange={(e) => {
                                                   const value = maskDate(e.target.value);
                                                   setForm((prev) => ({ ...prev, date: value }));
                                               }}/>
                                        <input ref={datePickerRef} type='date' tabIndex={-1} aria-hidden='true'
                                               className='deliveryNote__date-picker'
                                               onChange={(e) => {
                                                   const value = isoToDisplay(e.target.value);
                                                   if (value) setForm((prev) => ({ ...prev, date: value }));
                                               }}/>
                                    </span>
                                ) : (
                                    <input className='input ttnForm__input'
                                           type='text'
                                           placeholder={f.placeholder}
                                           value={form[f.key]}
                                           onChange={(e) => {
                                               const value = e.target.value;
                                               setForm((prev) => ({ ...prev, [f.key]: value }));
                                           }}/>
                                )}
                            </label>
                        ))}
                    </fieldset>
                    <div className='deliveryNote__footer'>
                        {error && <span className='ttnForm__error'>{error}</span>}
                        {canEdit && (
                            <button type='submit' className='nav__button btn-primary' disabled={disabled}>
                                {saving ? 'Формируется…' : note ? 'Сохранить и пересоздать pdf' : 'Сформировать pdf'}
                            </button>
                        )}
                    </div>
                </form>
            )}
        </div>
    );
};

export default DeliveryNoteSection;
